import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Grid, Card, CardContent, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { getArticles } from '../../utils/requests';
import dayjs from 'dayjs';

// Styled components
const SectionTitle = styled(Typography)({
  fontSize: '40px',
  fontWeight: 500,
  lineHeight: 1.2,
});

const ViewAllButton = styled(Button)({
  fontSize: '16px',
  color: '#333',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'transparent',
  },
});

const ArticleCard = styled(Card)({
  boxShadow: 'none',
  borderRadius: '10px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  transition: 'all 0.3s ease',
  '&:hover': {
    '& img': {
      transform: 'scale(1.05)',
    },
    '& .article-title': {
      color: '#F4762A',
    }
  }
});

const ArticleImage = styled('img')({
  width: '100%',
  borderRadius: '10px',
  objectFit: 'cover',
  transition: 'transform 0.3s ease',
});

const ArticleTitle = styled(Typography)({
  fontSize: '23px',
  fontWeight: 500,
  lineHeight: '130%',
  marginBottom: '8px',
  transition: 'color 0.3s ease',
});

const ArticleDescription = styled(Typography)({
  fontSize: '16px',
  fontWeight: 400,
  color: '#717172',
  marginBottom: '16px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
});

const ArticleDate = styled(Typography)({
  fontSize: '14px',
  fontWeight: 400,
  color: '#9F9FA0',
  position: 'absolute',
  bottom: '16px',
  left: '16px',
});

// Interface for article item
interface ArticleItem {
  id: number;
  image: string;
  title: string;
  description: string;
  date: string;
  link: string;
}

interface ApiArticleItem {
  news_ID: number;
  news_Images: string;
  news_Title_TH: string;
  news_Title_EN: string;
  news_Title_CN: string;
  news_Des_TH: string;
  news_Des_EN: string;
  news_Des_CN: string;
  news_Create: string;
  news_Update: string;
  category_ID: number;
  [key: string]: any;
}

interface NewsArticleSectionProps {
  viewAllLink?: string;
  articles?: ArticleItem[];
}

const NewsArticleSection: React.FC<NewsArticleSectionProps> = ({ 
  viewAllLink = "/article",
  articles = []
}) => {
  const { t, i18n } = useTranslation(['home']);
  const [loading, setLoading] = useState<boolean>(true);
  const [apiArticles, setApiArticles] = useState<ArticleItem[]>([]);
  const locale = i18n.language || 'th';

  // 1. ใช้ React.useMemo เพื่อไม่ให้สร้าง defaultArticles ใหม่ทุกครั้งที่ render
  const defaultArticles = React.useMemo<ArticleItem[]>(() => [
    {
      id: 1,
      image: '/assets/homePage/mock_article1.webp',
      title: '5 วิธีดูบ้านก่อนซื้อ ตรวจสอบทุกรายละเอียดก่อนผิดพลาด',
      description: 'การซื้อบ้านเป็นการลงทุนครั้งใหญ่ในชีวิต ดังนั้นการตรวจสอบรายละเอียดต่างๆ อย่างละเอียดก่อนตัดสินใจซื้อจึงเป็นสิ่งสำคัญมาก เพื่อหลีกเลี่ยงปัญหาที่อาจเกิดขึ้นในอนาคต',
      date: '10 มกราคม 2566',
      link: '/article/1'
    },
    {
      id: 2,
      image: '/assets/homePage/mock_article2.webp',
      title: 'บ้านเดี่ยวหรือทาวน์โฮม? เลือกแบบไหนให้เหมาะกับไลฟ์สไตล์',
      description: 'การเลือกระหว่างบ้านเดี่ยวและทาวน์โฮมเป็นคำถามที่หลายคนต้องเผชิญ แต่ละประเภทมีข้อดีและข้อเสียแตกต่างกัน',
      date: '8 มกราคม 2566',
      link: '/article/2'
    },
    {
      id: 3,
      image: '/assets/homePage/mock_article3.webp',
      title: 'โอกาสและความเสี่ยงของการลงทุนในอสังหาริมทรัพย์ปี 2566',
      description: 'ตลาดอสังหาริมทรัพย์กำลังเผชิญกับความท้าทายและโอกาสใหม่ๆ ในปี 2566 นักลงทุนควรพิจารณาปัจจัยต่างๆ',
      date: '5 มกราคม 2566',
      link: '/article/3'
    },
    {
      id: 4,
      image: '/assets/homePage/mock_article4.webp',
      title: 'แนวโน้มตลาดคอนโดมิเนียมในกรุงเทพฯ และปริมณฑล',
      description: 'ตลาดคอนโดมิเนียมในเขตกรุงเทพฯ และปริมณฑลมีการเปลี่ยนแปลงอย่างต่อเนื่อง ทั้งในแง่ของอุปสงค์ อุปทาน และราคา',
      date: '1 มกราคม 2566',
      link: '/article/4'
    },
    {
      id: 5,
      image: '/assets/homePage/mock_article5.webp',
      title: 'การสร้างรายได้จากการปล่อยเช่าอสังหาริมทรัพย์ ทำอย่างไรให้คุ้มค่า',
      description: 'การลงทุนในอสังหาริมทรัพย์เพื่อปล่อยเช่าสามารถสร้างรายได้แบบ passive income ได้อย่างดี แต่ต้องมีการวางแผนและบริหารจัดการที่ดี',
      date: '28 ธันวาคม 2565',
      link: '/article/5'
    },
  ], []);

  // 2. ใช้ useEffect เพื่อดึงข้อมูลบทความจาก API เมื่อคอมโพเนนต์ถูกโหลด
  useEffect(() => {
    // 3. ใช้ AbortController เพื่อยกเลิกการร้องขอเมื่อคอมโพเนนต์ถูก unmount
    const controller = new AbortController();
    
    const fetchArticles = async () => {
      try {
        setLoading(true);
        // 4. ใช้ getArticles API ด้วยพารามิเตอร์ที่กำหนด
        const response = await getArticles(1, 5);
        
        if (response && response.data) {
          // 5. แปลงข้อมูลจาก API เป็นรูปแบบที่คอมโพเนนต์ต้องการ
          const formattedArticles: ArticleItem[] = response.data.map((article: ApiArticleItem) => {
            const langUpper = locale.toUpperCase();
            return {
              id: article.news_ID,
              image: article.news_Images ? `https://cdn.9asset.com/download/article_image/${article.news_ID}/${article.news_Images}` : '/assets/single-logo.png',
              title: article[`news_Title_${langUpper}`] || article.news_Title_TH,
              description: article[`news_Des_${langUpper}`] || article.news_Des_TH,
              date: dayjs(article.news_Create).format('DD MMMM YYYY'),
              link: `/article/${article.news_ID}/${encodeURIComponent(article[`news_Title_${langUpper}`] || article.news_Title_TH).replace(/%20/g, '-')}`
            };
          });
          
          setApiArticles(formattedArticles);
        }
      } catch (error) {
        console.error('Failed to fetch articles:', error);
        // ในกรณีที่เกิดข้อผิดพลาด ใช้ข้อมูลเริ่มต้น
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();

    // 6. ทำความสะอาดเมื่อคอมโพเนนต์ถูก unmount
    return () => {
      controller.abort();
    };
  }, [locale]); // 7. ดึงข้อมูลใหม่เมื่อภาษาเปลี่ยน

  // 8. ตัดสินใจว่าจะใช้ข้อมูลจากไหน โดยมีลำดับความสำคัญ: articles props > API data > default data
  const displayArticles = articles.length > 0 ? articles : (apiArticles.length > 0 ? apiArticles : defaultArticles);

  const handleViewAll = () => {
    window.location.href = viewAllLink;
  };

  // 9. ใช้ React.useMemo เพื่อไม่ให้คำนวณ mainArticle และ secondaryArticles ใหม่ทุกครั้งที่ render
  const [mainArticle, secondaryArticles] = React.useMemo(() => {
    const main = displayArticles[0];
    const secondary = displayArticles.slice(1, 5);
    return [main, secondary];
  }, [displayArticles]);

  // 10. แสดง skeleton ในขณะที่กำลังโหลดข้อมูล
  if (loading) {
    return (
      <Box sx={{ 
        backgroundColor: '#FFFFFF',
        padding: '80px 0',
      }}>
        <Box sx={{ 
          maxWidth: '1496px',
          margin: '0 auto',
          padding: '0',
          height: '832px'
        }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '40px'
          }}>
            <Skeleton variant="text" width={300} height={60} />
            <Skeleton variant="text" width={150} height={40} />
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ height: '574px', borderRadius: '10px' }}>
                <Skeleton variant="rectangular" width="100%" height={574} />
              </Box>
              <Box sx={{ padding: '24px 16px' }}>
                <Skeleton variant="text" width="80%" height={60} />
                <Skeleton variant="text" width="100%" height={80} />
                <Skeleton variant="text" width="40%" height={30} />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Grid container spacing={4}>
                {[1, 2, 3, 4].map((item) => (
                  <Grid item xs={12} sm={6} key={item}>
                    <Box sx={{ height: '228px', borderRadius: '10px' }}>
                      <Skeleton variant="rectangular" width="100%" height={228} />
                    </Box>
                    <Box sx={{ padding: '16px' }}>
                      <Skeleton variant="text" width="80%" height={50} />
                      <Skeleton variant="text" width="100%" height={60} />
                      <Skeleton variant="text" width="40%" height={30} />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      backgroundColor: '#FFFFFF',
      padding: '80px 0',
    }}>
      <Box sx={{ 
        maxWidth: '1496px',
        margin: '0 auto',
        padding: '0',
        height: '832px'
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '40px'
        }}>
          <SectionTitle>{t('news_articles.title')}</SectionTitle>
          <ViewAllButton 
            endIcon={<ArrowCircleRightOutlinedIcon sx={{ color: '#F4762A' }} />} 
            onClick={handleViewAll}
          >
            {t('news_articles.view_all')}
          </ViewAllButton>
        </Box>

        <Grid container spacing={4}>
          {/* Main Article */}
          <Grid item xs={12} md={6}>
            <Link href={mainArticle.link} passHref style={{ textDecoration: 'none', color: 'inherit' }}>
              <ArticleCard>
                <Box sx={{ height: '574px', overflow: 'hidden', borderRadius: '10px' }}>
                  <ArticleImage
                    src={mainArticle.image}
                    alt={mainArticle.title}
                    height="574"
                    loading="lazy" // 11. ใช้ lazy loading สำหรับรูปภาพ
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                      e.currentTarget.src = '/assets/single-logo.png'; // 12. กำหนดรูปภาพสำรองเมื่อโหลดรูปผิดพลาด
                    }}
                  />
                </Box>
                <CardContent sx={{ padding: '24px 16px', flex: 1, position: 'relative', pb: '60px' }}>
                  <ArticleTitle className="article-title">{mainArticle.title}</ArticleTitle>
                  <ArticleDescription>{mainArticle.description}</ArticleDescription>
                  <ArticleDate>{mainArticle.date}</ArticleDate>
                </CardContent>
              </ArticleCard>
            </Link>
          </Grid>

          {/* Secondary Articles */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={4}>
              {secondaryArticles.map((article, index) => (
                <Grid item xs={12} sm={6} key={article.id}>
                  <Link href={article.link} passHref style={{ textDecoration: 'none', color: 'inherit' }}>
                    <ArticleCard sx={{ height: '396px' }}>
                      <Box sx={{ height: '228px', overflow: 'hidden', borderRadius: '10px' }}>
                        <ArticleImage
                          src={article.image}
                          alt={article.title}
                          height="228"
                          loading="lazy" // 13. ใช้ lazy loading สำหรับรูปภาพ
                          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                            e.currentTarget.src = '/assets/single-logo.png'; // 14. กำหนดรูปภาพสำรองเมื่อโหลดรูปผิดพลาด
                          }}
                        />
                      </Box>
                      <CardContent sx={{ padding: '16px', flex: 1, position: 'relative', pb: '60px' }}>
                        <ArticleTitle className="article-title">{article.title}</ArticleTitle>
                        <ArticleDescription>{article.description}</ArticleDescription>
                        <ArticleDate>{article.date}</ArticleDate>
                      </CardContent>
                    </ArticleCard>
                  </Link>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export { NewsArticleSection };
export default NewsArticleSection; 